/**
 * NotificationsSettings.tsx — T321 SETTINGS-1 / FR-004 rewrite.
 *
 * Wires the three master notification toggles to
 *   - api.organizations.getNotificationPreferences     (read)
 *   - api.organizations.updateNotificationPreferences  (admin-only mutation;
 *                                                       read-merge-write into
 *                                                       systemConfig:notifications)
 *
 * Honest-state contract: every Save is routed through useMutationWithToast
 * (FR-013 (H1), FR-004 (S3)). Logs argument KEYS only (never values).
 *
 * H4 ratchet: this file is no longer the thin re-export shell — the
 * standalone body satisfies the per-sub-page S2 test in
 * frontend-v3/tests/destub/honest-state.test.ts (FR-004).
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import { Btn, Panel, Toggle } from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { api } from '@convex/api';

interface NotificationPrefs {
  emailEnabled: boolean;
  slackEnabled: boolean;
  pagerDutyEnabled: boolean;
}

export function NotificationsSettings() {
  const current = useQuery(api.organizations.getNotificationPreferences) as
    | NotificationPrefs
    | undefined;

  const updateNotificationPreferencesM = useMutationWithToast(
    api.organizations.updateNotificationPreferences,
    {
      logLabel: 'organizations_updateNotificationPreferences',
      successMessage: 'Preferencias guardadas.',
      errorMessagePrefix: 'No se pudieron guardar las preferencias',
    },
  );

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [pagerDutyEnabled, setPagerDutyEnabled] = useState(false);

  useEffect(() => {
    if (!current) return;
    setEmailEnabled(Boolean(current.emailEnabled));
    setSlackEnabled(Boolean(current.slackEnabled));
    setPagerDutyEnabled(Boolean(current.pagerDutyEnabled));
  }, [current]);

  const handleSave = () => {
    void updateNotificationPreferencesM.run({
      emailEnabled,
      slackEnabled,
      pagerDutyEnabled,
    });
  };

  const isSaving = updateNotificationPreferencesM.loading;
  const isLoading = current === undefined;

  return (
    <div>
      <Panel style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>
              Notificaciones por correo
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
              Recibir alertas y resúmenes por correo electrónico.
            </div>
          </div>
          <Toggle
            on={emailEnabled}
            onChange={() => setEmailEnabled((v) => !v)}
            disabled={isLoading || isSaving}
          />
        </div>
      </Panel>
      <Panel style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>
              Notificaciones de Slack
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
              Enrutar alertas a tu canal de Slack.
            </div>
          </div>
          <Toggle
            on={slackEnabled}
            onChange={() => setSlackEnabled((v) => !v)}
            disabled={isLoading || isSaving}
          />
        </div>
      </Panel>
      <Panel style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 15 }}>
              Escalación a PagerDuty
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
              Despertar a la guardia para incidentes críticos.
            </div>
          </div>
          <Toggle
            on={pagerDutyEnabled}
            onChange={() => setPagerDutyEnabled((v) => !v)}
            disabled={isLoading || isSaving}
          />
        </div>
      </Panel>
      <div>
        <Btn
          variant="solid"
          onClick={handleSave}
          disabled={isLoading || isSaving}
        >
          {isSaving ? 'Guardando…' : 'Guardar'}
        </Btn>
      </div>
    </div>
  );
}

export default NotificationsSettings;
